import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { HealthModeProvider } from './context/HealthModeContext';
import { DashboardProvider } from './context/DashboardContext';
import { SubscriptionProvider } from './context/SubscriptionContext';
import DashboardAwareHeader from './components/DashboardAwareHeader';
import ImpersonationBanner from './components/ImpersonationBanner';
import ProtectedRoute from './components/ProtectedRoute';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import ResetPassword from './pages/ResetPassword';
import AuthSuccess from './pages/AuthSuccess';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import AdminPanel from './pages/AdminPanel';
import Subscription from './pages/Subscription';
import InstallPWAPopup from './components/InstallPWAPopup';
// import UpdateNotificationManager from './components/UpdateNotificationManager'; // ELIMINADO: Sistema simplificado
import { useAdminNotifications } from './hooks/useAdminNotifications';
import { useWebPush } from './hooks/useWebPush';
import './App.css';


const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  
  // Hook para escuchar notificaciones del admin (solo para usuarios autenticados)
  useAdminNotifications();
  
  // Hook para manejar Web Push notifications
  useWebPush();

  if (isLoading) {
    return (
      <div className="app-loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Cargando aplicación...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      {isAuthenticated ? (
        <DashboardProvider>
          <DashboardAwareHeader />
          <ImpersonationBanner />
          <main className="main-content">
            <Routes>
              <Route 
                path="/" 
                element={<Navigate to="/dashboard" replace />} 
              />
              <Route 
                path="/login" 
                element={<Navigate to="/dashboard" replace />} 
              />
              <Route 
                path="/register" 
                element={<Navigate to="/dashboard" replace />} 
              />
              <Route 
                path="/auth/success" 
                element={<AuthSuccess />} 
              />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/*"
                element={
                  <ProtectedRoute requireAdmin>
                    <AdminPanel />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/subscription"
                element={
                  <ProtectedRoute>
                    <Subscription />
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </main>
          
          {/* Popup de instalación PWA - Solo para usuarios autenticados */}
          <InstallPWAPopup />
        </DashboardProvider>
      ) : (
        <main className="main-content">
          <Routes>
            <Route
              path="/"
              element={<Landing />}
            />
            <Route
              path="/login"
              element={<Login />}
            />
            <Route
              path="/register"
              element={<Register />}
            />
            <Route
              path="/reset-password"
              element={<ResetPassword />}
            />
            <Route
              path="/auth/success"
              element={<AuthSuccess />}
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      )}
    </div>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
        <ThemeProvider>
            <HealthModeProvider>
              <SubscriptionProvider>
                <AppContent />
              </SubscriptionProvider>
            </HealthModeProvider>
        </ThemeProvider>
      </AuthProvider>
    </Router>
  );
};

export default App;
